// One continuous-contour family (100-unit square): consistent hairline weight and spacing, marine ~30% over the course gradient, one deep-coral line. Each category bends the contours into its cue.
const M = "#0B2E44", C = "#B8326C", GAP = 7.5;

const rng = (seedStr) => {
  let a = 0;
  for (const ch of String(seedStr)) a = (Math.imul(a ^ ch.charCodeAt(0), 2654435761) + 0x9e3779b9) | 0;
  return () => {
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
};
const f = (n) => n.toFixed(1);
const range = (a, b, step) => { const o = []; for (let v = a; v <= b + 1e-6; v += step) o.push(v); return o; };
const line = (pts) => `M${pts.map(([x, y]) => `${f(x)} ${f(y)}`).join(" L")}`;
const curve = (fn, t0 = -10, t1 = 110, n = 48) => line(range(0, n, 1).map((i) => fn(t0 + ((t1 - t0) * i) / n)));
const circle = (cx, cy, r, a0 = 0, a1 = Math.PI * 2) => line(range(0, 64, 1).map((i) => { const a = a0 + ((a1 - a0) * i) / 64; return [cx + r * Math.cos(a), cy + r * Math.sin(a)]; }));
const pick = (paths, k, extra = "") => paths.map((d, i) => `<path d="${d}" stroke="${i === k ? C : M}" stroke-opacity="${i === k ? 0.85 : 0.3}" stroke-width="${i === k ? 1.9 : 1.3}" vector-effect="non-scaling-stroke"${extra}/>`).join("");

const SYSTEMS = {
  fodmap: (R) => { const ph = R() * 6; const ys = range(-6, 106, GAP); return pick(ys.map((y) => curve((x) => [x, y + 7 * Math.sin(x / 16 + ph + y / 40)])), Math.floor(ys.length / 2)); },
  ibs_friendly: (R) => { const ph = R() * 6; const ys = range(4, 100, GAP); return pick(ys.map((y, i) => curve((x) => [x, y + 7 * (1 - i / (ys.length - 1)) ** 1.6 * Math.sin(x / 9 + ph)])), Math.floor(ys.length * 0.55)); },
  low_histamine: (R) => { const cx = 44 + R() * 12, cy = 44 + R() * 12; const rs = range(4, 80, GAP); return pick(rs.map((r) => circle(cx, cy, r)), 2); },
  catechol_free: (R) => {
    const ph = R() * 2, out = [];
    [[25, 25], [75, 25], [25, 75], [75, 75], [50, 50], [0, 50], [100, 50], [50, 0], [50, 100]].forEach(([cx, cy]) => range(5, 25, 6.5).forEach((r, j) => out.push(circle(cx, cy, r + Math.sin(j + ph) * 1.2))));
    return pick(out, 4 * 4 + 1);
  },
  nut_free: (R) => {
    const cx = 46 + R() * 8, cy = 46 + R() * 8, a = 20, gap = 0.5 + R() * 0.3;
    const flow = range(-6, 106, GAP).map((y0) => curve((x) => { const dx = x - cx, dy = y0 - cy, r2 = dx * dx + dy * dy + 1; return [x, y0 + (dy * a * a * 1.6) / (r2 + a * a)]; }));
    return pick(flow, -1) + pick([circle(cx, cy, 14, gap, Math.PI * 2 - gap), circle(cx, cy, 9, gap + 0.3, Math.PI * 2 - gap - 0.3)], 0);
  },
  low_sulphur: (R) => {
    const xs = range(4, 100, GAP), k = Math.floor(xs.length / 2), ph = R() * 6, out = [], dots = [];
    xs.forEach((x, i) => { const top = 18 + 30 * (0.5 + 0.5 * Math.sin(i * 0.9 + ph)); out.push(curve((t) => [x + 2.5 * Math.sin(t / 14 + i), t], top, 110, 24)); dots.push(`<circle cx="${f(x + 2.5 * Math.sin(top / 14 + i))}" cy="${f(top - 3)}" r="1.6" fill="${i === k ? C : M}" fill-opacity="${i === k ? 0.85 : 0.35}"/>`); });
    return pick(out, k) + dots.join("");
  },
  vegan: (R) => {
    const rot = -35 + R() * 10, out = range(6, 46, 6).map((s) => { const w = (t) => Math.sin(((t + 46) / 92) * Math.PI) ** 0.9 * s * 0.55; const up = curve((t) => [50 + (t * s) / 46, 50 - w(t)], -46, 46, 32); const dn = curve((t) => [50 + (t * s) / 46, 50 + w(t)], -46, 46, 32); return `${up} ${dn}`; });
    out.push(line([[-6, 50], [106, 50]]));
    return `<g transform="rotate(${f(rot)} 50 50)">${pick(out, out.length - 1)}</g>`;
  },
  lactose_gluten: (R) => { const tilt = 0.35 + R() * 0.15; const ys = range(-40, 140, GAP); return pick(ys.map((y) => curve((x) => [x, y - x * tilt + 2 * Math.sin(x / 12)])), Math.floor(ys.length / 2), ` stroke-dasharray="7 3.5"`); },
  broths: (R) => { const ph = R() * 6; const xs = range(2, 100, GAP); return pick(xs.map((x, i) => curve((t) => [x + (1 - t / 100) ** 2 * 6 * Math.sin(t / 7 + ph + i * 0.6), t], -4, 104, 40)), Math.floor(xs.length / 2)); },
  beverage: (R) => {
    const cx = 30 + R() * 40, cy = 30 + R() * 40, turns = 11, pts = range(0, turns * 64, 1).map((i) => { const a = (i / 64) * Math.PI * 2, r = 2 + (GAP * a) / (Math.PI * 2); return [cx + r * Math.cos(a), cy + r * Math.sin(a)]; });
    return pick([line(pts.slice(0, 160)), line(pts.slice(159))], 0);
  },
};

export const PATTERN_KEYS = Object.keys(SYSTEMS);

export const patternSvg = (k, seed) => {
  const sys = SYSTEMS[k];
  if (!sys) return "";
  const R = rng(`${k}:${seed}`);
  const rot = (R() - 0.5) * 10;
  return `<svg viewBox="0 0 100 100" fill="none" stroke-linecap="round" stroke-linejoin="round" width="100%" height="100%" aria-hidden="true" preserveAspectRatio="xMidYMid slice"><g class="cp-drift"><g transform="rotate(${f(rot)} 50 50) scale(1.08) translate(-3.7 -3.7)">${sys(R)}</g></g></svg>`;
};
